import { useSessionContext } from "@/contexts/SessionContext";
import { useTaskWatchingContext } from "@/contexts/TaskWatchingContext";
import { useSidebarNavigation } from "@/contexts/SidebarNavigationContext";
import { useFilteredNotifications } from "@/hooks/useFilteredNotifications";
import { PatchTask } from "@/services/fhir/task/PatchTask";
import { currentFeatures, oids } from "@/configs/Settings";
import { getPatientIdForSummaryFromTask } from "@/utils/taskNotificationUtils";
import { NotificationsOutlined } from "@mui/icons-material";
import { Badge, IconButton } from "@mui/material";
import type { Task } from "fhir/r5";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAppointmentReferenceFromQuestionnaireTask } from "src-next/modules/questionnaire-builder/questionnaire-tasks";

import { DesktopNotificationsPanel } from "./DesktopNotificationsPanel";
import { MobileNotificationsPanel } from "./MobileNotificationsPanel";
import { useHeaderCollapse } from "./HeaderCollapseContext";

const HeaderNotification: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => {
  const { user, access } = useSessionContext();
  const { setIsSidebarExpanded } = useSidebarNavigation();
  const {
    mobileOpenPanel,
    toggleMobileHeaderPanel,
    closeMobileHeaderPanel,
  } = useHeaderCollapse();
  const { allTasks, updateTaskInContext } = useTaskWatchingContext();
  const navigate = useNavigate();
  const { filteredTasks, unreadCount } = useFilteredNotifications({
    allTasks,
    user,
    access,
  });

  const myPatientReference = useMemo(() => {
    const username = user?.username;
    if (typeof username !== "string" || !/^\d{11}$/.test(username)) {
      return "";
    }
    return `Patient/${oids.cpf}-${username}`;
  }, [user?.username]);

  const [updatingById, setUpdatingById] = useState<Record<string, boolean>>({});
  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<null | HTMLElement>(null);

  const handleToggleReadStatus = async (task: Task) => {
    const taskId = task.id;
    if (!taskId) return;
    try {
      setUpdatingById((prev) => ({ ...prev, [taskId]: true }));
      const isCompleted = task?.status === "completed";
      const nextStatus = isCompleted ? "requested" : "completed";

      await PatchTask(taskId, [
        { op: "replace", path: "/status", value: nextStatus },
      ]);
      updateTaskInContext(taskId, { status: nextStatus });
    } catch (error) {
      console.error("Erro ao marcar tarefa como lida:", error);
    } finally {
      setUpdatingById((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  const handleAccessMedicalRecord = async (task: Task) => {
    const taskId = task?.id;
    if (!taskId) return;
    try {
      setUpdatingById((prev) => ({ ...prev, [taskId]: true }));

      const patientId = getPatientIdForSummaryFromTask(task, {
        isDefaultApp: Boolean(currentFeatures.defaultApp),
      });
      if (!patientId) {
        console.warn(
          "Não foi possível derivar patientId para /summary a partir da task",
          task,
        );
        return;
      }

      if (task?.status !== "completed") {
        await PatchTask(taskId, [
          { op: "replace", path: "/status", value: "completed" },
        ]);
        updateTaskInContext(taskId, { status: "completed" });
      }

      if (isMobile) {
        closeMobileHeaderPanel();
      } else {
        setNotificationAnchorEl(null);
      }
      navigate("/summary", { state: { patientId } });
    } catch (error) {
      console.error("Erro ao acessar prontuário via notificação:", error);
    } finally {
      setUpdatingById((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  const handleRespondQuestionnaire = useCallback(
    (task: Task) => {
      const focusReference = task.focus?.reference?.trim() ?? "";
      const questionnaireId = focusReference.startsWith("Questionnaire/")
        ? focusReference.replace("Questionnaire/", "").trim()
        : "";

      const patientTaskId = task.id?.trim() ?? "";
      const subjectReference = myPatientReference.trim();
      const requesterPractitionerRoleReference =
        task.requester?.reference?.trim() ?? "";
      const ownerOrganizationReference = task.owner?.reference?.trim() ?? "";
      const questionnaireTitle = (task.description || "Questionário").trim();
      const appointmentReference =
        getAppointmentReferenceFromQuestionnaireTask(task);

      if (
        !questionnaireId ||
        !patientTaskId ||
        !subjectReference ||
        !requesterPractitionerRoleReference ||
        !ownerOrganizationReference
      ) {
        toast.error(
          "Não foi possível abrir o questionário (dados da task incompletos).",
        );
        return;
      }

      setIsSidebarExpanded(false);
      if (isMobile) {
        closeMobileHeaderPanel();
      } else {
        setNotificationAnchorEl(null);
      }
      navigate("/questionnaire-respond", {
        state: {
          questionnaireId,
          questionnaireTitle,
          subjectReference,
          patientTaskId,
          requesterPractitionerRoleReference,
          ownerOrganizationReference,
          ...(appointmentReference ? { appointmentReference } : {}),
        },
      });
    },
    [closeMobileHeaderPanel, isMobile, myPatientReference, navigate, setIsSidebarExpanded],
  );

  const handleCloseNotifications = useCallback(() => {
    if (isMobile) {
      closeMobileHeaderPanel();
      return;
    }
    setNotificationAnchorEl(null);
  }, [closeMobileHeaderPanel, isMobile]);

  const isNotificationsOpen = isMobile
    ? mobileOpenPanel === "notifications"
    : Boolean(notificationAnchorEl);

  const panelProps = {
    open: isNotificationsOpen,
    onClose: handleCloseNotifications,
    unreadCount,
    loading: false,
    accessType: access?.type,
    tasks: filteredTasks,
    updatingById,
    onToggleReadStatus: (task: Task) => {
      void handleToggleReadStatus(task);
    },
    onAccessMedicalRecord: (task: Task) => {
      void handleAccessMedicalRecord(task);
    },
    onRespondQuestionnaire: handleRespondQuestionnaire,
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={(event) => {
          event.stopPropagation();
          if (isMobile) {
            toggleMobileHeaderPanel("notifications");
            return;
          }
          setNotificationAnchorEl(
            notificationAnchorEl ? null : event.currentTarget,
          );
        }}
        sx={{ mr: 1 }}
        aria-label="Abrir notificações"
      >
        <Badge badgeContent={unreadCount || 0} color="error">
          <NotificationsOutlined />
        </Badge>
      </IconButton>

      {isMobile ? (
        <MobileNotificationsPanel {...panelProps} />
      ) : (
        <DesktopNotificationsPanel
          {...panelProps}
          anchorEl={notificationAnchorEl}
        />
      )}
    </>
  );
};

export { HeaderNotification };
